// frontend/src/components/client/ClientFuelRequests.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, Fuel, Calendar, X, RefreshCw } from 'lucide-react';
import { getFuelRequests, createFuelRequest, cancelFuelRequest, getMyAircraft } from '../../services/clientApi';

const StatusBadge = ({ status }) => {
  const config = {
    pending: { label: 'En attente', color: 'bg-yellow-500/20 text-yellow-400' },
    approved: { label: 'Approuvé', color: 'bg-green-500/20 text-green-400' },
    completed: { label: 'Terminé', color: 'bg-blue-500/20 text-blue-400' },
    rejected: { label: 'Refusé', color: 'bg-red-500/20 text-red-400' },
    cancelled: { label: 'Annulé', color: 'bg-gray-500/20 text-gray-400' }
  };
  const { label, color } = config[status] || config.pending;
  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>{label}</span>;
};

const ClientFuelRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [aircraftList, setAircraftList] = useState([]);
  const [formData, setFormData] = useState({ aircraft_id: '', quantity: '', scheduled_date: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const intervalRef = useRef(null);

  const loadRequests = useCallback(async (showRefreshAnimation = false) => {
    if (showRefreshAnimation) setRefreshing(true);
    else setLoading(true);
    try {
      const response = await getFuelRequests();
      setRequests(response.data);
    } catch (error) {
      console.error('Error loading fuel requests:', error);
    } finally {
      setLoading(false);
      if (showRefreshAnimation) setRefreshing(false);
    }
  }, []);

  const loadAircraft = async () => {
    try {
      const response = await getMyAircraft();
      setAircraftList(response.data);
    } catch (error) { console.error('Error loading aircraft:', error); }
  };

  useEffect(() => {
    loadRequests();
    loadAircraft();
    intervalRef.current = setInterval(() => loadRequests(), 60000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [loadRequests]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.aircraft_id || !formData.quantity || !formData.scheduled_date) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setSubmitting(true);
    try {
      await createFuelRequest(formData);
      setShowModal(false);
      setFormData({ aircraft_id: '', quantity: '', scheduled_date: '', description: '' });
      await loadRequests();
    } catch (error) {
      console.error('Error creating fuel request:', error);
      alert('Erreur lors de la création de la demande');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    if (window.confirm('Annuler cette demande ?')) {
      try {
        await cancelFuelRequest(id);
        await loadRequests();
      } catch (error) {
        alert("Erreur lors de l'annulation");
      }
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="px-4 sm:px-0">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0 mr-3">
          <h2 className="text-xl font-bold text-white">Demandes de carburant</h2>
          <p className="text-gray-400 text-sm">Gérez vos demandes de fuel</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={() => loadRequests(true)}
            disabled={refreshing}
            className="p-2.5 bg-gray-700 hover:bg-gray-600 active:bg-gray-500 rounded-lg text-white transition disabled:opacity-50"
            title="Rafraîchir"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="p-2.5 sm:px-4 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 rounded-lg text-white transition flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nouvelle demande</span>
          </button>
        </div>
      </div>

      <div className="text-right mb-3">
        <span className="text-xs text-gray-500">Actualisation auto toutes les 60s</span>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 bg-gray-800/50 rounded-xl border border-gray-700">
          <Fuel className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500">Aucune demande de carburant</p>
          <button onClick={() => setShowModal(true)} className="mt-3 text-blue-400 hover:text-blue-300 text-sm active:opacity-70">
            Créer une demande
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => (
            <div key={req.id} className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-orange-500/20 rounded-lg flex-shrink-0">
                  <Fuel className="w-5 h-5 text-orange-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                      <span className="text-white font-medium">{req.quantity} gallons</span>
                      <StatusBadge status={req.status} />
                    </div>
                    {req.status === 'pending' && (
                      <button
                        onClick={() => handleCancel(req.id)}
                        className="p-1.5 hover:bg-red-500/20 active:bg-red-500/30 rounded-lg text-red-400 transition flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-gray-400 mt-1 truncate">
                    {req.tail_number} – {req.manufacturer} {req.model}
                  </p>
                  <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                    <Calendar className="w-3 h-3 flex-shrink-0" />
                    <span>{formatDate(req.scheduled_date)}</span>
                  </div>
                  {req.description && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{req.description}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FAB for mobile */}
      <button
        onClick={() => setShowModal(true)}
        className="fixed bottom-6 right-6 sm:hidden w-14 h-14 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 rounded-full text-white shadow-lg flex items-center justify-center transition z-40"
        style={{ boxShadow: '0 4px 20px rgba(59,130,246,0.5)' }}
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center z-50">
          <div className="bg-gray-800 rounded-t-2xl sm:rounded-xl w-full sm:max-w-md border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-center pt-3 pb-1 sm:hidden">
              <div className="w-10 h-1 bg-gray-600 rounded-full" />
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 sticky top-0 bg-gray-800 z-10">
              <h3 className="text-white font-semibold">Nouvelle demande de carburant</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Avion *</label>
                <select
                  value={formData.aircraft_id}
                  onChange={(e) => setFormData({ ...formData, aircraft_id: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base"
                  required
                >
                  <option value="">Sélectionner un avion</option>
                  {aircraftList.map((a) => (
                    <option key={a.id} value={a.id}>{a.tail_number} – {a.manufacturer} {a.model}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Quantité (gallons) *</label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Date souhaitée *</label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white resize-none text-base"
                  placeholder="Informations complémentaires..."
                />
              </div>
              <div className="flex flex-col gap-3 pt-2 pb-2">
                <button type="submit" disabled={submitting}
                  className="w-full py-3.5 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 rounded-xl text-white font-semibold disabled:opacity-50 transition text-base">
                  {submitting ? 'Envoi...' : 'Envoyer la demande'}
                </button>
                <button type="button" onClick={() => setShowModal(false)}
                  className="w-full py-3.5 bg-gray-700 hover:bg-gray-600 active:bg-gray-500 rounded-xl text-white transition text-base">
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientFuelRequests;